import {
  beautifyDurationUnit,
  calculateFinancialSupport,
  calculateIntakeName,
  toCurrency,
} from "../../../helpers";
import { programDetailsConfig, PROGRAM_TYPES } from "../constants";
import { calcFeeWithScholarshipAndFinancialAid } from "./helperFunctions";
import {
  IGetProgramBackEndModel,
  IPanel,
  IPreviewOtherFee,
  IProgramPreviewModel,
} from "../types";

export function parseToProgramPreviewHigher({
  backEndData,
  majors,
  branches,
}: {
  backEndData: IGetProgramBackEndModel;
  majors: { id: string; name: string }[];
  branches: { id: string; name: string }[];
}): IProgramPreviewModel {
  // Finance --------------------------------------------------
  const currency = +backEndData.currency;
  const tuition = +backEndData.tuition;
  const branchNames = backEndData.branches.map(
    (b) => branches.find((branch) => branch.id === b.branchId)!.name,
  );

  const {
    feeWithCurrency: tuitionWithCurrency,
    calcFeeWithCurrency: calcTuitionWithCurrency,
    scholarshipWithCurrency: tuitionScholarshipWithCurrency,
    financialAidWithCurrency: tuitionFinancialAidWithCurrency,
    hasSupport: hasTuitionSupport,
  } = calcFeeWithScholarshipAndFinancialAid(
    tuition,
    backEndData.hasTuitionScholarship,
    backEndData.tuitionScholarshipType,
    backEndData.tuitionScholarshipAmount,
    backEndData.hasTuitionFinancialAid,
    backEndData.tuitionFinancialAidType,
    backEndData.tuitionFinancialAidAmount,
    currency,
  );

  // Commission
  const commissionAmount = calculateFinancialSupport(
    tuition,
    true,
    backEndData.commissionType,
    backEndData.commissionAmount,
  );

  // Other Fees -----------------------------------------------
  const otherFees: IPreviewOtherFee[] = [];
  let otherFeesSum = 0;
  backEndData.otherFees.forEach((fee) => {
    otherFees.push({
      name: fee.name,
      amount: toCurrency(fee.amount, currency, " ", "code"),
    });

    otherFeesSum += +fee.amount;
  });

  const {
    feeWithCurrency: otherFeesWithCurrency,
    calcFeeWithCurrency: calcOtherFeesWithCurrency,
    scholarshipWithCurrency: otherFeesScholarshipWithCurrency,
    financialAidWithCurrency: otherFeesFinancialAidWithCurrency,
    hasSupport: hasOtherFeesSupport,
  } = calcFeeWithScholarshipAndFinancialAid(
    otherFeesSum,
    backEndData.hasOtherFeesScholarship,
    backEndData.otherFeesScholarshipType,
    backEndData.otherFeesScholarshipAmount,
    backEndData.hasOtherFeesFinancialAid,
    backEndData.otherFeesFinancialAidType,
    backEndData.otherFeesFinancialAidAmount,
    currency,
  );

  //Details
  const details: IPanel[] = [];

  programDetailsConfig.forEach((config) => {
    const foundDetail = backEndData.details.find(
      (detail) => detail.type === config.type,
    );

    if (foundDetail) {
      details.push({
        type: config.type,
        typeName: config.name,
        content: foundDetail.content,
      });
    }
  });

  //Detail Files
  const detailFiles = backEndData.detailFiles!.map((file) => ({
    id: file.fileId,
    name: file.name!,
    size: file.size!,
    path: file.path!,
  }));

  return {
    programType: PROGRAM_TYPES.find(
      (type) => type.id === +backEndData.programType,
    )!.name,
    commissionAmount: toCurrency(commissionAmount, currency, " ", "code"),
    majorName: majors.find((m) => m.id === backEndData.majorId)!.name,
    branches: branchNames,
    otherFeesFinancialAid: otherFeesFinancialAidWithCurrency,
    otherFeesScholarship: otherFeesScholarshipWithCurrency,
    tuitionFinancialAid: tuitionFinancialAidWithCurrency,
    tuitionScholarship: tuitionScholarshipWithCurrency,
    durationUnit: beautifyDurationUnit(
      backEndData.durationAmount,
      backEndData.durationUnit,
      "Program",
    ),
    durationAmount: "" + backEndData.durationAmount,
    tuitionDurationAmount: backEndData.tuitionDurationAmount
      ? "" + backEndData.tuitionDurationAmount
      : "",
    tuitionDurationUnit: beautifyDurationUnit(
      backEndData.tuitionDurationAmount,
      backEndData.tuitionDurationUnit,
      "Program",
    ),
    calculatedOtherFees: calcOtherFeesWithCurrency,
    intakes: backEndData.intakes.map((intake) => ({
      deadline: intake.deadline,
      startDate: intake.startDate,
      intake: calculateIntakeName(intake.startDate),
    })),
    calculatedTuition: calcTuitionWithCurrency,
    otherFeesSum: otherFeesWithCurrency,
    tuition: tuitionWithCurrency,
    exams: backEndData.exams,
    detailFiles: detailFiles,
    otherFees: otherFees,
    details: details,
    hasTuitionSupport,
    hasOtherFeesSupport,
  };
}
